if (!customElements.get('back-in-stock')) {
  customElements.define(
    'back-in-stock',
    class BackInStock extends HTMLElement {
      // Klaviyo API revisions are retired two years after release; bump this when it nears that.
      static API_REVISION = '2025-07-15';
      static API_URL = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';

      constructor() {
        super();
        this.onToggle = this.onToggle.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
      }

      connectedCallback() {
        this.toggle = this.querySelector('.back-in-stock__toggle');
        this.panel = this.querySelector('.back-in-stock__panel');
        this.fields = this.querySelector('.back-in-stock__fields');
        this.input = this.querySelector('input[type="email"]');
        this.submitButton = this.querySelector('.back-in-stock__submit');
        this.status = this.querySelector('.back-in-stock__status');
        this.signupForm = document.getElementById(this.dataset.signupFormId);
        this.eligible = new Set((this.dataset.eligibleVariants || '').split(',').filter(Boolean));
        this.variantInput = document.getElementById(this.dataset.formId)?.querySelector('input[name="id"]');

        this.toggle.addEventListener('click', this.onToggle);
        this.signupForm?.addEventListener('submit', this.onSubmit);
        this.variantInput?.addEventListener('change', this.onVariantChange);
      }

      disconnectedCallback() {
        this.toggle?.removeEventListener('click', this.onToggle);
        this.signupForm?.removeEventListener('submit', this.onSubmit);
        this.variantInput?.removeEventListener('change', this.onVariantChange);
      }

      onVariantChange() {
        const variantId = this.variantInput.value;
        if (variantId === this.dataset.variantId) return;

        this.dataset.variantId = variantId;
        this.hidden = !this.eligible.has(variantId);
        this.reset();
      }

      onToggle() {
        const expanded = this.toggle.getAttribute('aria-expanded') === 'true';
        this.setExpanded(!expanded);
        if (!expanded && !this.fields.hidden) this.input.focus();
      }

      setExpanded(expanded) {
        this.toggle.setAttribute('aria-expanded', String(expanded));
        this.panel.hidden = !expanded;
      }

      reset() {
        this.setExpanded(false);
        this.fields.hidden = false;
        this.setStatus('');
      }

      setStatus(message, isError = false) {
        this.status.textContent = message;
        this.status.classList.toggle('back-in-stock__status--error', isError);
        this.input.toggleAttribute('aria-invalid', isError);
      }

      setLoading(loading) {
        this.submitButton.toggleAttribute('disabled', loading);
        this.submitButton.classList.toggle('loading', loading);
        this.submitButton.setAttribute('aria-busy', String(loading));
      }

      async onSubmit(event) {
        event.preventDefault();

        const email = this.input.value.trim();
        if (!email || !this.input.checkValidity()) {
          this.setStatus(this.dataset.errorEmail, true);
          this.input.focus();
          return;
        }

        const variantId = this.dataset.variantId;
        if (!this.dataset.companyId || !variantId) {
          console.error('back-in-stock: missing Klaviyo public API key (Theme settings → Back in stock) or variant id');
          this.setStatus(this.dataset.errorGeneric, true);
          return;
        }

        this.setLoading(true);
        this.setStatus('');

        try {
          const response = await fetch(`${BackInStock.API_URL}?company_id=${encodeURIComponent(this.dataset.companyId)}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/vnd.api+json',
              revision: BackInStock.API_REVISION,
            },
            body: JSON.stringify({
              data: {
                type: 'back-in-stock-subscription',
                attributes: {
                  profile: { data: { type: 'profile', attributes: { email } } },
                  channels: ['EMAIL'],
                },
                relationships: {
                  variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } },
                },
              },
            }),
          });

          if (!response.ok) throw new Error(`Klaviyo responded ${response.status}: ${await response.text()}`);

          this.fields.hidden = true;
          this.setStatus(this.dataset.success);
          this.toggle.focus();
        } catch (error) {
          console.error('back-in-stock:', error);
          this.setStatus(this.dataset.errorGeneric, true);
        } finally {
          this.setLoading(false);
        }
      }
    }
  );
}
